import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Search, X, Filter } from 'lucide-react';
import { Card } from '@/components/ui/card';

const TaskFilters = ({ users, onFilter, onClear }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignmentFilter, setAssignmentFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    applyFilters();
  }, [searchQuery, statusFilter, assignmentFilter, userFilter, locationFilter]);

  const applyFilters = () => {
    const filters = {};
    
    if (searchQuery.trim()) {
      filters.query = searchQuery.trim();
    }
    
    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }
    
    if (assignmentFilter && assignmentFilter !== 'all') {
      filters.assigned = assignmentFilter;
    } else if (userFilter && userFilter !== 'all') {
      filters.assigned = userFilter;
    }
    
    if (locationFilter.trim()) {
      filters.location = locationFilter.trim();
    }

    onFilter(filters);
  };

  const handleClear = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setAssignmentFilter('all');
    setUserFilter('all');
    setLocationFilter('');
    setShowFilters(false);
    onClear();
  };

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || 
                          assignmentFilter !== 'all' || userFilter !== 'all' || 
                          locationFilter;

  return (
    <Card className="p-4 mb-6">
      <div className="space-y-4">
        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by task name, user, or description..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            className="gap-2"
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4" />
            Filters
          </Button>
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={handleClear}
              className="gap-2 text-red-600 hover:text-red-700"
              data-testid="clear-filters-btn"
            >
              <X className="w-4 h-4" />
              Clear
            </Button>
          )}
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t">
            {/* Status Filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Status
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="overdue">Overdue</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Assignment Filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Assignment
              </label>
              <Select value={assignmentFilter} onValueChange={setAssignmentFilter}>
                <SelectTrigger data-testid="assignment-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tasks</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* User Filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Field Staff
              </label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger data-testid="user-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.filter(u => u.role === 'field_staff').map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Location Filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Location (Pincode/Address)
              </label>
              <Input
                placeholder="Search location..."
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                data-testid="location-filter"
              />
            </div>
          </div>
        )}

        {/* Active Filters Summary */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2 border-t">
            <span className="text-xs font-medium text-slate-600">Active filters:</span>
            {searchQuery && (
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                Search: "{searchQuery}"
              </span>
            )}
            {statusFilter !== 'all' && (
              <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                Status: {statusFilter}
              </span>
            )}
            {assignmentFilter !== 'all' && (
              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                {assignmentFilter}
              </span>
            )}
            {userFilter !== 'all' && (
              <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded">
                User: {users.find(u => u.id === userFilter)?.name}
              </span>
            )}
            {locationFilter && (
              <span className="text-xs bg-pink-100 text-pink-800 px-2 py-1 rounded">
                Location: {locationFilter}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default TaskFilters;
